import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBKOAcao } from '@/hooks/useBKOAcao';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { aplicarEscopoTriagem } from '@/components/kanban/triagem/triagemPedidoUtils';
import { rpcTriagem } from '@/components/kanban/triagem/rpcTriagem';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { posicaoNaFila } from '@/lib/filaTriagem';
import { depoisDoClique, manterNaTriagem, marcarTelasDeForaDesatualizadas, meuUsuarioId } from '@/components/kanban/triagem/acaoTriagem';

export interface ItemTriagemCriacao {
  item_id: string;
  produto_id: string | null;
  produto: string;
  familia: string;
  quantidade: number;
  plano_valor: number;
  subtotal: number;
}

export interface PedidoTriagemCriacao {
  pedido_id: string;
  numero_pedido: string;
  cnpj: string;
  /** CNPJ ou CPF — a fila mostra um segmento por vez (`segmentoTriagem.ts`). */
  tipo_pessoa: string | null;
  /** CNPJ ou CPF, como está no banco. Máscara em `formatarDocumento`. */
  documento: string;
  razao_social: string;
  consultor_nome: string;
  equipe_nome: string;
  entrada_coluna_em: string;
  /** Desde quando esta disponivel para criar (chegada em PEDIDO FECHADO). E' o
   *  relogio do TEMPO e da POSICAO na fila. Douglas 21/08/2026. */
  entrada_fila_criacao_em: string | null;
  valor_total: number;
  lead_origem_id: string | null;
  telefone: string | null;
  cliente_id: string | null;
  manter_em: string | null;
  /** Quem PEGOU o pedido (dono da criação). null = ainda livre. */
  dono_id: string | null;
  dono_nome: string;
  itens: ItemTriagemCriacao[];
}

export function pedidoEhFibra(pedido: PedidoTriagemCriacao) {
  return pedido.itens.length > 0 && pedido.itens.every((item) => {
    const familia = item.familia.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
    return familia === 'FIBRA' || familia === 'FIXA BASICA';
  });
}

/* Fibra e CPF n\u00e3o t\u00eam biometria nem aceite (Douglas 14/10/2026): criou, vai
   para o Input; a fibra que n\u00e3o deu para criar vai para Aguardando HP. A r\u00e9gua
   \u00e9 a mesma da RPC `triagem_sem_biometria`. */
export function pedidoSemBiometria(pedido: PedidoTriagemCriacao) {
  return pedido.tipo_pessoa === 'PF' || pedidoEhFibra(pedido);
}

export type DestinoSemBiometria = 'INPUT' | 'AGUARDANDO HP';

export function useTriagemCriacaoPedido() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { executarAcaoConsolidadaAsync } = useBKOAcao();
  const { profile } = useAuth();
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'B',
    baseInterval: POLLING_INTERVALS.PESO_B,
  });

  const { data: pedidos = [], isLoading, refetch } = useQuery({
    queryKey: ['triagem-criacao-pedido', profile?.id],
    queryFn: async () => {
      let q = supabase
        .from('v_planilha_mae')
        .select('item_id, pedido_id, numero_pedido, cnpj, razao_social, consultor_nome, equipe_nome, produto_id, produto, quantidade, plano_valor, subtotal, entrada_coluna_em, entrada_fila_criacao_em, lead_origem_id, contato_real_telefone, telefone_principal, cliente_whatsapp, manter_em, cliente_id, bko_id, bko_atual_nome, tipo_pessoa, documento')
        .eq('coluna_atual', 'CRIAÇÃO PEDIDO')
        .eq('quadro_atual', 'CONTRATO_PRIME')
        .order('entrada_coluna_em', { ascending: true })
        .order('pedido_id', { ascending: true });
      // Escopo por papel — mesma régua do Kanban (BKO vê só as equipes mapeadas)
      q = await aplicarEscopoTriagem(q, profile);
      const { data, error } = await q;

      if (error) {
        onQueryError(error);
        throw error;
      }
      onQuerySuccess();

      // O mapa de tipos nao conhece todas as colunas da v_planilha_mae, entao o
      // select inteiro cai em SelectQueryError. Estreita para o que se usa aqui.
      const linhasComProduto = (data || []) as unknown as Array<{ produto_id?: string | null }>;
      const produtoIds = Array.from(new Set(linhasComProduto.map((item) => item.produto_id).filter((id): id is string => !!id)));
      const familiasPorProduto = new Map<string, string>();
      if (produtoIds.length > 0) {
        const { data: produtos, error: produtosError } = await supabase
          .from('produtos')
          .select('id, familia')
          .in('id', produtoIds);
        if (produtosError) throw produtosError;
        for (const produto of produtos || []) familiasPorProduto.set(produto.id, produto.familia);
      }

      const porPedido = new Map<string, PedidoTriagemCriacao>();
      for (const item of (data || []) as any[]) {
        const subtotal = Number(item.subtotal) || (Number(item.plano_valor) || 0) * (Number(item.quantidade) || 1);
        let pedido = porPedido.get(item.pedido_id);
        if (!pedido) {
          pedido = {
            pedido_id: item.pedido_id,
            numero_pedido: String(item.numero_pedido || ''),
            cnpj: item.cnpj || '',
            tipo_pessoa: item.tipo_pessoa ?? null,
            documento: item.documento || item.cnpj || '',
            razao_social: item.razao_social || '',
            consultor_nome: item.consultor_nome || '',
            equipe_nome: item.equipe_nome || '',
            entrada_coluna_em: item.entrada_coluna_em || '',
            entrada_fila_criacao_em: item.entrada_fila_criacao_em || null,
            valor_total: 0,
            lead_origem_id: item.lead_origem_id || null,
            telefone: item.contato_real_telefone || item.telefone_principal || item.cliente_whatsapp || null,
            cliente_id: item.cliente_id || null,
            manter_em: item.manter_em || null,
            dono_id: item.bko_id || null,
            dono_nome: item.bko_atual_nome || '',
            itens: [],
          };
          porPedido.set(item.pedido_id, pedido);
        }
        if (item.entrada_fila_criacao_em && (!pedido.entrada_fila_criacao_em || item.entrada_fila_criacao_em < pedido.entrada_fila_criacao_em)) {
          pedido.entrada_fila_criacao_em = item.entrada_fila_criacao_em;
        }
        if (item.entrada_coluna_em && (!pedido.entrada_coluna_em || item.entrada_coluna_em < pedido.entrada_coluna_em)) {
          pedido.entrada_coluna_em = item.entrada_coluna_em;
        }
        if (item.manter_em && (!pedido.manter_em || item.manter_em > pedido.manter_em)) {
          pedido.manter_em = item.manter_em;
        }
        pedido.valor_total += subtotal;
        pedido.itens.push({
          item_id: item.item_id,
          produto_id: item.produto_id || null,
          produto: item.produto || '',
          familia: item.produto_id ? (familiasPorProduto.get(item.produto_id) || '') : '',
          quantidade: Number(item.quantidade) || 0,
          plano_valor: Number(item.plano_valor) || 0,
          subtotal,
        });
      }

      // Fila FIFO por ordem de chegada. O marco e' a chegada em PEDIDO FECHADO
      // (`entrada_fila_criacao_em`), NAO a entrada na coluna CRIAÇÃO: o tempo
      // parado em PEDIDO FECHADO passa a contar. Sem isso dava pra segurar o
      // pedido la' e so' puxar quando desse pra criar rapido — media de 6,1h
      // escondidas, 30% acima do prazo inteiro de 5h. (Douglas 21/08/2026)
      // Adiado com justificativa continua indo para o fim, contando do adiamento.
      // MESMO relogio do TEMPO exibido — antes eram dois, e por isso a tela
      // mostrava gente com mais tempo embaixo.
      return Array.from(porPedido.values()).sort(
        (a, b) =>
          posicaoNaFila(a.entrada_fila_criacao_em || a.entrada_coluna_em, a.manter_em) -
          posicaoNaFila(b.entrada_fila_criacao_em || b.entrada_coluna_em, b.manter_em),
      );
    },
    refetchInterval,
  });

  const invalidateAllQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['triagem-criacao-pedido'] });
    queryClient.invalidateQueries({ queryKey: ['kanban-optimized'] });
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
    queryClient.invalidateQueries({ queryKey: ['planilha-itens'] });
    queryClient.invalidateQueries({ queryKey: ['v_planilha_mae'] });
    queryClient.invalidateQueries({ queryKey: ['timeline-unificada'] });
  };

  // Grava cada link como evento 'biometria' na timeline (tabela movimento),
  // numerando a tentativa a partir do que já existe para o pedido
  const registrarLinksBiometria = async (pedido: PedidoTriagemCriacao, links: string[]) => {
    if (links.length === 0) return;
    const [usuarioId, { count, error: countError }] = await Promise.all([
      meuUsuarioId(),
      supabase
        .from('movimento')
        .select('id', { count: 'exact', head: true })
        .eq('entidade', 'pedido')
        .eq('entidade_id', pedido.pedido_id)
        .eq('tipo', 'biometria'),
    ]);
    if (countError) throw countError;

    // Todos os links num insert só, na ordem em que foram colados.
    const base = Number(count || 0);
    const { error } = await supabase.from('movimento').insert(links.map((link, i) => {
      const tentativa = base + i + 1;
      return {
        entidade: 'pedido',
        entidade_id: pedido.pedido_id,
        tipo: 'biometria',
        descricao: `Biometria — Link ${tentativa}`,
        motivo: `Link ${tentativa} da biometria registrado`,
        cnpj: pedido.cnpj || null,
        usuario_id: usuarioId,
        icone: '🪪',
        destaque: true,
        payload: { link, tentativa, numero_pedido: pedido.numero_pedido },
      };
    }) as never);
    if (error) throw error;
  };

  // Pegar e liberar não tiram o pedido da fila: relê só a triagem.
  const releFila = () => {
    void queryClient.invalidateQueries({ queryKey: ['triagem-criacao-pedido'] });
    void queryClient.invalidateQueries({ queryKey: ['timeline-unificada'] });
    marcarTelasDeForaDesatualizadas(queryClient);
  };

  const depoisDeAndar = (pedidoId: string, modo: 'sai' | 'desce') => {
    void queryClient.invalidateQueries({ queryKey: ['timeline-unificada'] });
    return depoisDoClique(queryClient, ['triagem-criacao-pedido'], { pedidoId }, modo);
  };

  // Pedido que chegou sem dono (recuperado, ou de antes da lei de 18/08): a
  // mesma lei do Pedido Fechado vale aqui — PEGA primeiro, faz depois. Sem isto
  // dois BKO enxergam a mesma linha livre liberada e criam o mesmo contrato.
  const pegarNaCriacao = useMutation({
    mutationFn: async (pedido: PedidoTriagemCriacao) => {
      if (!profile?.id) throw new Error('Sessão sem usuário — recarregue a página.');
      const { error } = await rpcTriagem('pegar_pedido_para_criacao', {
        p_pedido_id: pedido.pedido_id,
        p_bko_id: profile.id,
      });
      if (error) throw new Error(error.message || 'Não foi possível pegar o pedido.');
      return { numeroPedido: pedido.numero_pedido };
    },
    onSuccess: ({ numeroPedido }) => {
      toast({
        title: 'Pedido é seu',
        description: `${numeroPedido} entrou na SUA fila. A contagem começou agora.`,
      });
      releFila();
    },
    onError: (error) => {
      toast({
        title: 'Não deu para pegar o pedido',
        description: error instanceof Error ? error.message : 'Atualize a tela e tente de novo.',
        variant: 'destructive',
      });
    },
  });

  // Liberar o pedido preso no nome de alguém (supervisora do Backoffice e
  // gestão): o pedido volta a ficar livre e qualquer BKO sem pedido seu pega.
  const liberarPedido = useMutation({
    mutationFn: async (pedido: PedidoTriagemCriacao) => {
      if (!pedido.dono_id) throw new Error('Este pedido já está livre.');
      const { error } = await rpcTriagem('soltar_pedido_criacao', {
        p_pedido_id: pedido.pedido_id,
        p_bko_id: pedido.dono_id,
      });
      if (error) throw new Error(error.message || 'Não foi possível liberar o pedido.');
      return { numeroPedido: pedido.numero_pedido, dono: pedido.dono_nome };
    },
    onSuccess: ({ numeroPedido, dono }) => {
      toast({
        title: 'Pedido liberado',
        description: `${numeroPedido} saiu do nome de ${dono || 'quem tinha pegado'} e voltou para a fila livre.`,
      });
      releFila();
    },
    onError: (error) => {
      toast({
        title: 'Não deu para liberar',
        description: error instanceof Error ? error.message : 'Atualize a tela e tente de novo.',
        variant: 'destructive',
      });
    },
  });

  // "Passar para o fim da fila" (padrão Manter): justificativa/PRD viram Nota na
  // timeline e os itens ganham manter_em, numa ida e numa transação
  // (`fn_triagem_manter`; sem a nota gravada, não pula)
  const manterNoFim = useMutation({
    mutationFn: async ({ pedido, justificativa, prd }: { pedido: PedidoTriagemCriacao; justificativa: string; prd?: string }) => {
      await manterNaTriagem({
        pedidoId: pedido.pedido_id,
        itens: pedido.itens.map((i) => i.item_id),
        coluna: 'CRIAÇÃO PEDIDO',
        nota: `🕐 Triagem Criação — passado para o fim da fila: ${justificativa}${prd ? ` (PRD ${prd})` : ''}`,
      });
      return { pedidoId: pedido.pedido_id, numeroPedido: pedido.numero_pedido };
    },
    onSuccess: ({ pedidoId, numeroPedido }) => {
      toast({
        title: 'Passado para o fim da fila',
        description: `Pedido ${numeroPedido} adiado com justificativa — o próximo da fila liberou.`,
      });
      void depoisDeAndar(pedidoId, 'desce');
    },
    onError: (error) => {
      toast({
        title: 'Erro ao passar para o fim da fila',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // Move TODOS os itens do pedido para BIOMETRIA (mesma engrenagem do BKO:
  // webhook "Pedido Criado" consolidado + histórico + entrada_coluna_em) e
  // registra os links na timeline
  const enviarParaBiometria = useMutation({
    mutationFn: async ({ pedido, links }: { pedido: PedidoTriagemCriacao; links: string[] }) => {
      // Pedido que chegou sem dono (recuperado, ou de antes da lei de 18/08):
      // quem cria vira o dono, com data e hora — a marcação segue a verdade.
      if (!pedido.dono_id && profile?.id) {
        const { error: pegarError } = await rpcTriagem('pegar_pedido_para_criacao', {
          p_pedido_id: pedido.pedido_id,
          p_bko_id: profile.id,
        });
        if (pegarError) {
          throw new Error(pegarError.message || 'Não foi possível assumir o pedido.');
        }
      }

      const resultado = await executarAcaoConsolidadaAsync({
        itensIds: pedido.itens.map((i) => i.item_id),
        pedidoId: pedido.pedido_id,
        colunaAtual: 'CRIAÇÃO PEDIDO',
        origemTriagem: true,
      });

      let linksOk = true;
      try {
        if (resultado.proximaColuna !== 'INPUT') await registrarLinksBiometria(pedido, links);
      } catch (err) {
        console.error('Erro ao registrar links de biometria:', err);
        linksOk = false;
      }

      return { pedidoId: pedido.pedido_id, numeroPedido: pedido.numero_pedido, itens: pedido.itens.length, linksOk, destino: resultado.proximaColuna };
    },
    onSuccess: ({ pedidoId, numeroPedido, itens, linksOk, destino }) => {
      if (linksOk) {
        toast({
          title: destino === 'INPUT' ? 'Aceite confirmado · enviado para Input' : 'Enviado para Biometria',
          description: `Pedido ${numeroPedido} (${itens} ite${itens !== 1 ? 'ns' : 'm'}) movido para ${destino}. Registro disponível na timeline.`,
        });
      } else {
        toast({
          title: 'Pedido movido, mas os links não foram registrados',
          description: 'Registre o link da biometria na timeline do pedido manualmente.',
          variant: 'destructive',
        });
      }
      void depoisDeAndar(pedidoId, 'sai');
    },
    onError: (error) => {
      toast({
        title: 'Erro ao enviar para biometria',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  const moverSemBiometria = useMutation({
    mutationFn: async ({ pedido, destino }: { pedido: PedidoTriagemCriacao; destino: DestinoSemBiometria }) => {
      if (!pedidoSemBiometria(pedido)) {
        throw new Error('Pedido de móvel CNPJ segue pela Biometria.');
      }
      if (destino === 'AGUARDANDO HP' && !pedidoEhFibra(pedido)) {
        throw new Error('Aguardando HP é só para pedido de fibra.');
      }
      const { error } = await rpcTriagem('triagem_sem_biometria', {
        p_pedido_id: pedido.pedido_id,
        p_coluna_origem: 'CRIAÇÃO PEDIDO',
        p_coluna_destino: destino,
      });
      if (error) {
        throw new Error(error.message || 'Não foi possível mover o pedido.');
      }
      return { pedidoId: pedido.pedido_id, numeroPedido: pedido.numero_pedido, itens: pedido.itens.length, destino };
    },
    onSuccess: ({ pedidoId, numeroPedido, itens, destino }) => {
      toast({
        title: destino === 'INPUT' ? 'Pedido criado · enviado para Input' : 'Enviado para Aguardando HP',
        description: `Pedido ${numeroPedido} (${itens} ite${itens !== 1 ? 'ns' : 'm'}) movido para ${destino}.`,
      });
      void depoisDeAndar(pedidoId, 'sai');
    },
    onError: (error) => {
      toast({
        title: 'O pedido não andou',
        description: error instanceof Error ? error.message : 'Atualize a triagem e tente novamente.',
        variant: 'destructive',
      });
    },
  });

  return {
    pedidos,
    isLoading,
    refetch,
    invalidateAllQueries,
    enviarParaBiometria: enviarParaBiometria.mutateAsync,
    moverSemBiometria: moverSemBiometria.mutateAsync,
    manterNoFim: manterNoFim.mutateAsync,
    liberarPedido: liberarPedido.mutateAsync,
    pegarNaCriacao: pegarNaCriacao.mutateAsync,
  };
}
